// Fonts installed on this computer (the Local Font Access API, which Chromium and the desktop app
// have). They show in the font picker and shape with HarfBuzz like any other font. They stay on
// this machine: collaborators and the server don't have them unless someone uploads them.
import { collectionFace, invalidateTextMetrics, parseStyleName, type FontStyleInfo } from '@vellum/core';
import { app } from './app.ts';
import { fontsChanged } from './shaping.ts';

interface FontData {
  family: string;
  fullName: string;
  postscriptName: string;
  style: string;
  blob(): Promise<Blob>;
}

export interface LocalFace {
  family: string;
  style: string;
  weight: number;
  italic: boolean;
  postscriptName: string;
  data: FontData;
}

export const localFaces: LocalFace[] = [];
let status: 'unsupported' | 'idle' | 'loading' | 'ready' | 'denied' = typeof window !== 'undefined' && 'queryLocalFonts' in window ? 'idle' : 'unsupported';
const bytes = new Map<string, Promise<Uint8Array>>();

export function localFontStatus() {
  return status;
}

/**
 * List the fonts on this computer. Browsers ask the person first, so call this from a click
 * (`prompt`); at startup it only runs when permission was already given.
 */
export async function discoverLocalFonts(prompt = false): Promise<boolean> {
  if (status === 'unsupported' || status === 'loading') return false;
  if (status === 'ready') return true;
  if (!prompt) {
    const perm = await navigator.permissions?.query({ name: 'local-fonts' as PermissionName }).catch(() => null);
    if (perm?.state !== 'granted') return false;
  }
  status = 'loading';
  try {
    const list = (await (window as unknown as { queryLocalFonts(): Promise<FontData[]> }).queryLocalFonts()) ?? [];
    for (const f of list) {
      // hidden system faces (".SF NS…") aren't meant to be picked
      if (!f.family || f.family.startsWith('.')) continue;
      localFaces.push({ family: f.family, style: f.style, postscriptName: f.postscriptName, data: f, ...parseStyleName(f.style) });
    }
    status = 'ready';
    fontsChanged();
    invalidateTextMetrics();
    app.emit('ui');
    return true;
  } catch {
    status = 'denied';
    app.emit('ui');
    return false;
  }
}

/** Families on this computer, sorted (without ones the server's library already has). */
export function localFamilies(exclude: Set<string> = new Set()): string[] {
  return [...new Set(localFaces.map((f) => f.family))].filter((f) => !exclude.has(f.toLowerCase())).sort((a, b) => a.localeCompare(b));
}

export function localStyles(family: string): FontStyleInfo[] | null {
  const k = family.toLowerCase();
  const faces = localFaces.filter((f) => f.family.toLowerCase() === k);
  if (!faces.length) return null;
  const seen = new Set<string>();
  return faces.map((f) => ({ weight: f.weight, italic: f.italic })).filter((s) => !seen.has(`${s.weight}|${s.italic}`) && !!seen.add(`${s.weight}|${s.italic}`));
}

/** The font file for a style, as plain OpenType (one face out of a .ttc collection). */
export function localFontBytes(family: string, weight: number, italic: boolean): Promise<Uint8Array> | null {
  const k = family.toLowerCase();
  const face = localFaces.find((f) => f.family.toLowerCase() === k && f.weight === weight && f.italic === italic);
  if (!face) return null;
  let p = bytes.get(face.postscriptName);
  if (!p) {
    p = face.data.blob().then(async (b) => collectionFace(new Uint8Array(await b.arrayBuffer()), { postscriptName: face.postscriptName }));
    bytes.set(face.postscriptName, p);
  }
  return p;
}

/** Upload every style of a local family to the server's font library (the person's choice: check the license). */
export async function shareLocalFamily(family: string): Promise<void> {
  const { uploadFonts } = await import('./customFonts.ts');
  const k = family.toLowerCase();
  const faces = localFaces.filter((f) => f.family.toLowerCase() === k);
  const files: File[] = [];
  for (const f of faces) {
    const data = await localFontBytes(f.family, f.weight, f.italic);
    if (data) files.push(new File([data as BlobPart], `${f.postscriptName}.otf`, { type: 'font/otf' }));
  }
  if (files.length) await uploadFonts(files);
}
